import { Autocomplete } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { SortingState } from "@tanstack/react-table";
import ActionButton from "Common/Components/Buttons/ActionButton";
import ExportButton from "Common/Components/Buttons/ExportButton";
import ColumnVisibilityDropdown from "Common/Components/ColumnFilteringPopUp/ColumnFilteringPopUp";
import BootstrapTextField from "Common/Components/InputComponent/BootstrapTextField";
import LoaderErrorWrapper from "Common/Components/LoaderErrorWrapper/LoaderErrorWrapper";
import FormattedDate from "Common/Components/NameWithDate.tsx/FormattedDate";
import NameWithDate from "Common/Components/NameWithDate.tsx/NameWithDate";
import { defaultInnerStyle } from "Common/Components/PageContainer/PageContainer";
import SearchBox from "Common/Components/SearchBox/SearchBox";
import StatusBadge from "Common/Components/StatusBadge/StatusBadge";
import MasterTableContainer from "Common/Tables/MasterFiltersTable/MasterTableContainer";
import Colors, { MecordColors } from "Constants/Color";
import Chip from "components/Chips/Chip";
import CustomStyle, { autocompleteDropdownStyles } from "Constants/Style";
import { app_modules } from "helpers/apps_modules_list";
import usePermission from "hooks/usePermission";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createSelector } from "reselect";
import { getWarehouseByUser } from "slices/Masters/warehosue/thunk";
import { Visibility } from "@mui/icons-material";
import Tooltip from "@mui/material/Tooltip";

import {
  downloadDispatchChallanPdf,
  exportCSVDispatch,
  fetchWarehouseDispatch,
  revertDispatch,
} from "slices/Warehouse/Dispatch/thunk";
import { resetDispatchState } from "slices/Warehouse/Dispatch/reducer";
import { DynamicMenuItem } from "Common/Components/DynamicActionMenu/DynamicActionMenu";
import DeliveryDoneModal from "./Components/DeliveryDoneModal";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import UndoIcon from "@mui/icons-material/Undo";
import {
  showConfirm,
  showSuccess,
  showError,
  showLoading,
  hideLoading,
} from "helpers/swalHelper";
import { TableKeys } from "Constants/TableKeys/TableKeys";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import NoWarehousesMessage from "components/NoWarehouse/NoWarehouseMessage";
import { clearAllTableStates } from "utils/clearTableStates";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import StatusChip from "pages/Enquiry/Components/StatusChip";
import TruncatedTextWithTooltip from "Common/Components/TruncatedTextWithTooltip";

const WarehouseDispatch = () => {
  document.title = "Warehouse Dispatch List";
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const selectPRs = createSelector(
    (state: any) => state.WarehouseDispatchReducer,
    (data) => ({
      list: data.list,
      rowCount: data.count,
      loading: data.loading,
      error: data.error,
    }),
  );
  const { list, rowCount, loading, error } = useSelector(selectPRs);

  const { canView, canCreate, canUpdate } = usePermission(
    app_modules?.warehouse?.app_name,
    app_modules?.warehouse?.modules?.dispatch,
  );

  const tableState = usePreservedTableState({
    key: TableKeys.WAREHOUSE.DISPATCH,
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue,
    resetState,
  } = tableState;

  // const [pageIndex, setPageIndex] = useState(0);
  // const [pageSize, setPageSize] = useState(10);
  // const [columnFilters, setColumnFilters] = useState<any>([]);
  // const [filterValue, setFilterValue] = useState<any[]>([]);
  // const [sorting, setSorting] = useState<SortingState>([]);
  // const [searchInput, setSearchInput] = useState("");
  // const [search, setSearch] = useState("");
  const [data, setData] = useState([]);
  const [tableInstance, setTableInstance] = useState<any>(null);

  // Storage key for preserving warehouse selection
  const WAREHOUSE_STORAGE_KEY = "dispatch-selected-warehouse-id";

  // Initialize warehouseId from sessionStorage or null
  const [warehouseId, setWarehouseId] = useState<any>(() => {
    try {
      const stored = sessionStorage.getItem(WAREHOUSE_STORAGE_KEY);
      return stored ? stored : null;
    } catch {
      return null;
    }
  });

  // Save warehouseId to sessionStorage whenever it changes
  useEffect(() => {
    if (warehouseId) {
      try {
        sessionStorage.setItem(WAREHOUSE_STORAGE_KEY, warehouseId);
      } catch (error) {
        console.error("Failed to save warehouse selection:", error);
      }
    }
  }, [warehouseId]);

  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [selectedDispatchId, setSelectedDispatchId] = useState<string | null>(
    null,
  );
  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  // Get warehouse dropdown data from Redux
  const { warehouseByUserList, warehouseByUserLoading } = useSelector(
    (state: any) => state.warehouse,
  );
  useEffect(() => {
    if (warehouseId) {
      dispatch(
        fetchWarehouseDispatch({
          page: pageIndex + 1,
          pageSize,
          sort: sortField,
          order: sortField ? sortDirection : "",
          search,
          filters: filterValue,
          warehouse_id: warehouseId,
        }),
      );
    } else {
      dispatch(resetDispatchState());
      setData([]);
    }
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sorting,
    search,
    filterValue,
    warehouseId,
  ]);

  useEffect(() => {
    setData(list);
  }, [list]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  useEffect(() => {
    dispatch(getWarehouseByUser());
  }, [dispatch]);

  useEffect(() => {
    if (!warehouseByUserList?.length) {
      setWarehouseId(null);
      sessionStorage.removeItem(WAREHOUSE_STORAGE_KEY);
      return;
    }

    // If no warehouse is selected, or the selected warehouse is not in the list, set to first warehouse
    if (warehouseId == null) {
      setWarehouseId(warehouseByUserList[0].warehouse_id);
    } else {
      // Validate that the stored warehouseId still exists in the list
      const isValidWarehouse = warehouseByUserList.some(
        (warehouse: any) => warehouse.warehouse_id === warehouseId,
      );
      if (!isValidWarehouse) {
        // If stored warehouse is no longer valid, reset to first warehouse
        setWarehouseId(warehouseByUserList[0].warehouse_id);
      }
    }
  }, [warehouseByUserList, warehouseId]);

  const handleExport = async () => {
    if (!warehouseId) {
      throw new Error("Please select a warehouse");
    }
    return await exportCSVDispatch({
      filters: filterValue,
      warehouse_id: warehouseId,
    });
  }; 

  const handleDownloadChallan = async (
    dispatchId: string,
    dispatchNo: string,
  ) => {
    try {
      showLoading("Downloading PDF...");
      const res = await dispatch(
        downloadDispatchChallanPdf({ dispatchId, dispatchNo }),
      ).unwrap();
      if (res) {
        hideLoading();
        showSuccess("Challan downloaded successfully");
      }
    } catch (error: any) {
      hideLoading();
      showError(error?.message || error?.error || "Failed to download challan");
    }
  };

  const handleRevert = async (dispatch_id: string) => {
    const result = await showConfirm(
      "You want to revert this dispatch!",
      "Are you sure?",
      "Yes, revert it!",
      "Cancel",
    );

    if (result.isConfirmed) {
      try {
        const res = await dispatch(revertDispatch(dispatch_id)).unwrap();
        if (res) {
          await showSuccess("Dispatch has been reverted.", "Reverted!");
          dispatch(
            fetchWarehouseDispatch({
              page: pageIndex + 1,
              pageSize,
              sort: sortField,
              order: sortField ? sortDirection : "",
              search,
              filters: filterValue,
              warehouse_id: warehouseId,
            }),
          );
        }
      } catch (error: any) {
        console.error("Failed to revert dispatch", error);
        await showError("Failed to revert dispatch", "Error");
      }
    }
  };
  // Filter menu items to check if any will be visible
  // This ensures we only show the Action column if there are visible menu items
  const DynamicAction =
    canUpdate || canView
      ? [
          {
            header: "Action",
            enableColumnFilter: false,
            enableSorting: false,
            cell: ({ row }: { row: any }) => {
              const dispatch_id = row.original.dispatch_id;
              const dispatch_no = row.original.dispatch_no;
              const menuItems = [
                {
                  icon: Visibility,
                  label: "View",
                  permission: canView,
                  onClick: () => {
                    navigate(`/dispatch-view-details/${dispatch_id}`, {
                      state: { dispatch_id },
                    });
                  },
                  color: "primary",
                },
                {
                  icon: EditIcon,
                  label: "Edit",
                  permission:
                    canUpdate &&
                    row.original.status?.toLowerCase() !== "delivered",
                  onClick: () => {
                    navigate(`/edit-warehouse-dispatch/${dispatch_id}`, {
                      state: { warehouse_id: warehouseId },
                    });
                  },
                  color: "primary",
                },
                {
                  icon: FileDownloadOutlinedIcon,
                  label: "Download Challan",
                  permission: canView,
                  onClick: () =>
                    handleDownloadChallan(dispatch_id, dispatch_no),
                  color: "primary",
                },
                {
                  icon: CheckCircleIcon,
                  label: "Delivery Done",
                  permission:
                    canUpdate &&
                    (row.original.status?.toLowerCase() === "created" ||
                      row.original.status?.toLowerCase() === "dispatched"),
                  onClick: () => {
                    setSelectedDispatchId(dispatch_id);
                    setShowDeliveryModal(true);
                  },
                  color: "success",
                },
                {
                  icon: UndoIcon,
                  label: "Revert",
                  permission:
                    canUpdate &&
                    (row.original.status?.toLowerCase() === "created" ||
                      row.original.status?.toLowerCase() === "dispatched"),
                  onClick: () => {
                    handleRevert(dispatch_id);
                  },
                  color: "warning",
                },
              ];
              return (
                <DynamicMenuItem row={row.original} menuItems={menuItems} />
              );
            },
          },
        ]
      : [];

  const columns = useMemo(() => {
    const conditionalColumns = [
      // {
      //   header: "Sr No.",
      //   accessorKey: "sr_no",
      //   enableColumnFilter: false,
      //   enableSorting: true,
      // },
      {
        header: "Dispatch No.",
        accessorKey: "dispatch_no",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown" as const,
        filterFetchUrl: API_ENDPOINT?.WAREHOUSE?.DISPATCH?.FILTER_DROPDOWN(
          "dispatch_no",
          warehouseId,
        ),
        cell: ({ row }: { row: any }) => {
          return row.original.dispatch_no || "-";
        },
      },
      {
        header: "Dispatch Date",
        accessorKey: "dispatch_date",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const date = row.original.dispatch_date;
          return <FormattedDate date={date} />;
        },
      },
      {
        header: "Packing No.",
        accessorKey: "product_types__type__type_name",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.MASTERS?.PRODUCT?.FILTER_DROPDOWN(
          "product_types__type__type_name",
        )}`,
        cell: ({ row }: { row: any }) => {
          const dispatch_packings = row.original?.dispatch_packings;
          if (dispatch_packings?.length == 0) return "-";
          return (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "4px",
                minWidth: "150px",
              }}
            >
              {dispatch_packings?.map((data: any, index: number) => (
                <Chip>{data?.packing?.packing_number}</Chip>
              ))}
            </div>
          );
        },
      },
      {
        header: "SO No.",
        accessorKey: "so_number",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: API_ENDPOINT?.WAREHOUSE?.DISPATCH?.FILTER_DROPDOWN(
          "sales_order_product__sales_order_number",
          warehouseId,
        ),
        cell: ({ row }: { row: any }) => {
          return row.original?.sales_order_product?.sales_order_number || "-";
        },
      },
      {
        header: "Company Name",
        accessorKey: "company_name",
        enableColumnFilter: true,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: API_ENDPOINT?.WAREHOUSE?.DISPATCH?.FILTER_DROPDOWN(
          "company__company_name",
          warehouseId,
        ),
        cell: ({ row }: { row: any }) => {
          return row.original?.customer_details?.customer_name || "-";
        },
      },
      {
        header: "Dispatch Status",
        accessorKey: "status",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown" as const,
        filterFetchUrl: API_ENDPOINT?.WAREHOUSE?.DISPATCH?.FILTER_DROPDOWN(
          "status",
          warehouseId,
        ),
        filterOptionLabel: (option: any) => {
          // Display "created" as "dispatched" in the dropdown, but send "created" to backend
          const value = String(option);
          return value.toLowerCase() === "created" ? "dispatched" : value;
        },
        cell: ({ row }: { row: any }) => {
          let status = row.original.status;
          if (status?.toLowerCase() === "created") {
            status = "dispatched";
          }
          return <StatusChip status={status} />;

          const getStatusColor = (status: string) => {
            switch (status?.toLowerCase()) {
              case "created":
                return Colors?.bg_blue; // green
              case "dispatched":
                return Colors?.bg_blue; // green
              case "delivered":
                return Colors?.bg_green; // orange
              default:
                return "#ccc"; // fallback gray
            }
          };
          if (status?.toLowerCase() === "created") {
            status = "dispatched";
          }
          const displayText = status
            ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
            : "pending";

          return (
            <StatusBadge
              status={displayText}
              bgColor={status ? getStatusColor(status) : "#FF6C6C"}
              color="#fff"
            />
          );
        },
      },
      {
        header: "Initiated By",
        accessorKey: "initiated_by",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: API_ENDPOINT?.WAREHOUSE?.DISPATCH?.FILTER_DROPDOWN(
          "initiated_by",
          warehouseId,
        ),
        cell: ({ row }: { row: any }) => {
          return row.original?.initiated_by || "-";
        },
      },
      {
        header: "Shipping Address",
        accessorKey: "shipping_address_details",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        cell: ({ row }: { row: any }) => {
          const shipping_address = row?.original?.shipping_address_details;

          const fullAddress = shipping_address
            ? `${shipping_address?.address}, ${shipping_address?.city}, ${
                shipping_address?.state
              }, ${shipping_address?.country} - ${
                shipping_address?.pincode || shipping_address?.pin_code
              }`
            : "-";
          return (
            <div
              style={{
                whiteSpace: "normal",
                wordWrap: "break-word",
                width: "300px",
                display: "-webkit-box",
                WebkitLineClamp: 4,
                WebkitBoxOrient: "vertical",
              }}
            >
              <TruncatedTextWithTooltip text={fullAddress} />
            </div>
          );
        },
      },
      {
        header: "Delivered By",
        accessorKey: "delivered_by",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: API_ENDPOINT?.WAREHOUSE?.DISPATCH?.FILTER_DROPDOWN(
          "delivered_by",
          warehouseId,
        ),
        cell: ({ row }: { row: any }) => {
          return row.original?.delivered_by || "-";
        },
      },
      {
        header: "Delivered Date",
        accessorKey: "delivery_date",
        enableColumnFilter: true,
        filterType: "date" as const,
        enableSorting: true,
        cell: ({ row }: { row: any }) => {
          const date = row.original.delivery_date;
          return <FormattedDate date={date} />;
        },
      },
      {
        header: "Notes",
        accessorKey: "note",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        cell: ({ row }: { row: any }) => {
          const note = row.original.note;
          const noteItems = note?.split("\n") || [];

          return (
            <Tooltip
              title={
                <div style={{ whiteSpace: "pre-line" }}>
                  {noteItems.map((item: string, index: number) => (
                    <div key={index}>{item}</div>
                  ))}
                </div>
              }
              arrow
            >
              <div
                style={{
                  width: "300px",
                  display: "-webkit-box",
                  WebkitLineClamp: 4,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {noteItems.map((item: string, index: number) => (
                  <div key={index}>{item}</div>
                ))}
              </div>
            </Tooltip>
          );
        },
      },
      {
        header: "Tags",
        accessorKey: "tags",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const tags: string[] = row.original.tags || [];
          if (!tags.length) return "-";

          const maxVisible = 3;
          const visible = tags.slice(0, maxVisible);
          const remaining = tags.length - visible.length;
          const tooltipText = tags.join(", ");

          return (
            <Tooltip
              arrow
              placement="top"
              title={tooltipText}
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: 300,
                    fontSize: "13px",
                    whiteSpace: "normal",
                  },
                },
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "4px",
                  minWidth: "150px",
                  cursor: "pointer",
                }}
              >
                {visible.map((tag: string) => (
                  <Chip key={tag}>{tag}</Chip>
                ))}
                {remaining > 0 && <Chip>+{remaining} more</Chip>}
              </div>
            </Tooltip>
          );
        },
      },
      {
        header: "Created By/Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const user = row.original.created_by_user;
          return (
            <NameWithDate
              name={user?.username}
              date={row.original.created_at}
            />
          );
        },
      },
      {
        header: "Updated By/Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const user = row.original.updated_by_user;
          return (
            <NameWithDate
              name={user?.username}
              date={row.original.updated_at}
            />
          );
        },
      },
      ...DynamicAction,
    ];

    return [...conditionalColumns];
  }, [warehouseId, canUpdate, canView]);

  return (
    <div
      className="page-content px-3 mecord-kpi-list-page"
      style={{ "--mecord-table-offset": "360px" } as React.CSSProperties}
    >
      <div
        className="mecord-list-panel"
        style={{
          ...defaultInnerStyle,
          flex: "1 1 0",
          maxHeight: "100%",
          minHeight: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div className="flex-shrink-0">
          <div className="mecord-toolbar-title">
            <h6>Dispatch</h6>
            <Autocomplete
              options={warehouseByUserList ?? []}
              loading={warehouseByUserLoading}
              getOptionLabel={(option: any) => option?.warehouse_name || ""}
              value={
                warehouseByUserList.find(
                  (opt: any) => opt.warehouse_id === warehouseId,
                ) ?? null
              }
              isOptionEqualToValue={(option: any, value: any) =>
                option.warehouse_id === value.warehouse_id
              }
              onChange={(_, selected) => {
                clearAllTableStates();
                resetState();
                setWarehouseId(selected?.warehouse_id ?? null);
                setFilterValue([]);
                setColumnFilters([]);
              }}
              disableClearable
              sx={{ ...CustomStyle?.singleselect, width: 280 }}
              ListboxProps={autocompleteDropdownStyles.ListboxProps}
              renderOption={(props, option) =>
                autocompleteDropdownStyles.renderOption(
                  props,
                  option,
                  (opt) =>
                    typeof opt === "string"
                      ? opt
                      : String(opt.warehouse_name),
                )
              }
              renderInput={(params) => (
                <BootstrapTextField
                  {...params}
                  name="warehouse"
                  placeholder="Select Warehouse"
                  fullWidth
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: `${MecordColors?.ThemeBGColor} !important`,
                      },
                      "&:hover fieldset": {
                        borderColor: `${MecordColors?.ThemeBGColor} !important`,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: `${MecordColors?.ThemeBGColor} !important`,
                      },
                      fontFamily: "Poppins, sans-serif",
                      fontSize: "13px",
                      height: 34,
                    },
                  }}
                />
              )}
            />
          </div>

          <Row className="align-items-center mecord-search-row g-2">
            <SearchBox
              value={searchInput}
              onChange={(e: any) => setSearchInput(e.target.value)}
              placeholder="Search..."
            />
            <Col
              xs={12}
              md={12}
              lg={8}
              xl={7}
              className="d-flex justify-content-end align-items-center gap-2"
            >
              {tableInstance && (
                <ColumnVisibilityDropdown
                  columns={tableInstance.getAllLeafColumns()}
                  excludedColumns={["action", "sr_no"]}
                />
              )}
              {canView && <ExportButton onExport={handleExport} />}
              {canCreate && (
                <ActionButton
                  label="Add Dispatch"
                  icon="bi bi-plus-circle"
                  handleClick={() => {
                    navigate("/add-warehouse-dispatch", {
                      state: { warehouse_id: warehouseId },
                    });
                  }}
                  variant="primary"
                />
              )}
            </Col>
          </Row>
        </div>

        <div
          className="d-flex flex-column"
          style={{
            flex: "1 1 auto",
            minHeight: 0,
            maxHeight: "100%",
            marginTop: 4,
            overflow: "hidden",
          }}
        >
          {warehouseByUserList.length > 0 ? (
            <LoaderErrorWrapper loading={loading} error={error}>
              <MasterTableContainer
                columns={columns || []}
                data={data}
                totalRecords={rowCount}
                pageIndex={pageIndex}
                pageSize={pageSize}
                onPageChange={setPageIndex}
                onPageSizeChange={setPageSize}
                columnFilters={columnFilters}
                setColumnFilters={setColumnFilters}
                colValue={setFilterValue}
                sorting={sorting}
                onSortingChange={setSorting}
                isBordered={false}
                isPagination={true}
                isGlobalFilter={false}
                SearchPlaceholder="Search ..."
                manualSorting={true}
                onTableReady={setTableInstance}
              />
            </LoaderErrorWrapper>
          ) : (
            <NoWarehousesMessage />
          )}
        </div>
      </div>

      <DeliveryDoneModal
        show={showDeliveryModal}
        handleClose={() => setShowDeliveryModal(false)}
        dispatch_id={selectedDispatchId}
        warehouseId={warehouseId}
        pageIndex={pageIndex}
        pageSize={pageSize}
        sortField={sortField}
        sortDirection={sortDirection}
        search={search}
        filterValue={filterValue}
      />
    </div>
  );
};

export default WarehouseDispatch;
